import { useState } from 'react'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  KeyRound,
  KeySquare,
  LockKeyholeOpen,
  Power,
  PowerOff,
  UserCog,
} from 'lucide-react'
import {
  getAdminAccountsListQueryKey,
  getAdminAccountsRetrieveQueryKey,
  getV1MeRetrieveQueryOptions,
  useAdminAccountsChangeRole,
  useAdminAccountsDisable,
  useAdminAccountsEnable,
  useAdminAccountsPasswordReset,
  useAdminAccountsRetrieve,
  useV1MeRetrieve,
} from '@/api/client'
import { RoleEnum } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { AvatarPortrait } from '@/components/profile/dicebear-avatar'
import { avatarSeed } from '@/lib/avatar-catalog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AccountStateBadge } from '@/components/admin-accounts/account-state-badge'
import { roleLabel, stateLabel } from '@/lib/admin-account-labels'
import { ConfirmDialog } from '@/components/admin-accounts/confirm-dialog'
import { Reset2FADialog } from '@/components/admin-accounts/reset-2fa-dialog'
import { TemporaryPasswordDialog } from '@/components/admin-accounts/temporary-password-dialog'
import { formatExpiry, isPast } from '@/lib/temporary-password'
import { accountActionError } from '@/components/admin-accounts/account-error'

export const Route = createFileRoute('/_panel/konta-administracyjne/$kontoId')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: AdminAccountDetailPage,
})

type ActionKind = 'role' | 'disable' | 'enable' | 'reset' | null

function AdminAccountDetailPage() {
  const { kontoId } = Route.useParams()
  const id = Number(kontoId)
  const queryClient = useQueryClient()

  const { data: me } = useV1MeRetrieve()
  const myId = me?.status === 200 ? me.data.id : undefined

  const query = useAdminAccountsRetrieve(id)
  const account = query.data?.status === 200 ? query.data.data : undefined

  const [action, setAction] = useState<ActionKind>(null)
  const [error, setError] = useState<string | null>(null)
  const [resetDone, setResetDone] = useState(false)
  const [nextRole, setNextRole] = useState<RoleEnum>(RoleEnum.admin)
  const [reset2FAOpen, setReset2FAOpen] = useState(false)
  const [tempPasswordOpen, setTempPasswordOpen] = useState(false)

  const changeRole = useAdminAccountsChangeRole()
  const disable = useAdminAccountsDisable()
  const enable = useAdminAccountsEnable()
  const passwordReset = useAdminAccountsPasswordReset()

  const pending =
    changeRole.isPending || disable.isPending || enable.isPending || passwordReset.isPending

  const closeAction = () => {
    setAction(null)
    setError(null)
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: getAdminAccountsRetrieveQueryKey(id) })
    await queryClient.invalidateQueries({ queryKey: getAdminAccountsListQueryKey() })
  }

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Konto administracyjne" />
        <p className="p-6 text-sm text-muted-foreground">Ładowanie…</p>
      </>
    )
  }

  if (!account) {
    return (
      <>
        <PageHeader title="Konto administracyjne" />
        <p className="p-6 text-sm text-muted-foreground">Nie znaleziono konta.</p>
      </>
    )
  }

  const isSelf = myId !== undefined && myId === account.id
  const isDisabled = account.state === 'disabled'
  const canSetTemporaryPassword =
    !isSelf && (account.state === 'active' || account.state === 'needs_2fa')
  const tempExpiresAt = account.temporary_password_expires_at
  const tempExpired = tempExpiresAt ? isPast(tempExpiresAt) : false

  const runRoleChange = async () => {
    setError(null)
    const res = await changeRole.mutateAsync({ id, data: { role: nextRole } })
    if (res.status >= 400) {
      setError(accountActionError(res.data, res.status))
      return
    }
    await refresh()
    closeAction()
  }

  const runDisable = async () => {
    setError(null)
    const res = await disable.mutateAsync({ id })
    if (res.status >= 400) {
      setError(accountActionError(res.data, res.status))
      return
    }
    await refresh()
    closeAction()
  }

  const runEnable = async () => {
    setError(null)
    const res = await enable.mutateAsync({ id })
    if (res.status >= 400) {
      setError(accountActionError(res.data, res.status))
      return
    }
    await refresh()
    closeAction()
  }

  const runReset = async () => {
    setError(null)
    const res = await passwordReset.mutateAsync({ id })
    if (res.status >= 400) {
      setError(accountActionError(res.data, res.status))
      return
    }
    await refresh()
    setResetDone(true)
    closeAction()
  }

  const openRoleChange = () => {
    // Pre-select the *other* administrative role as the likely target.
    setNextRole(account.role === RoleEnum.superadmin ? RoleEnum.admin : RoleEnum.superadmin)
    setResetDone(false)
    setError(null)
    setAction('role')
  }

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon-sm">
              <Link to="/konta-administracyjne">
                <ArrowLeft />
              </Link>
            </Button>
            {/* alt pusty: adres jest tytułem tego widoku. */}
            <AvatarPortrait
              options={account.avatar}
              seed={avatarSeed(account.email)}
              alt=""
              size="md"
            />
            {account.email}
            <AccountStateBadge state={account.state} />
          </span>
        }
      />

      <div className="grid max-w-4xl gap-6 p-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Konto</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Row label="E-mail" value={account.email} />
            <Row label="Nick" value={account.nickname || '—'} />
            <Row label="Rola" value={roleLabel(account.role)} />
            <Row label="Stan" value={stateLabel(account.state)} />
            <Row label="Weryfikacja 2FA" value={account.has_2fa ? 'Włączona' : 'Wyłączona'} />
            <Row
              label="Wymaga zmiany hasła"
              value={
                <>
                  {account.force_password_change ? 'Tak' : 'Nie'}
                  {tempExpiresAt && (
                    <span className="block text-muted-foreground">
                      {tempExpired
                        ? 'hasło tymczasowe wygasło'
                        : `hasło tymczasowe ważne do ${formatExpiry(tempExpiresAt)}`}
                    </span>
                  )}
                </>
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Operacje</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-start gap-3">
            {isSelf && (
              <p className="text-sm text-muted-foreground">
                To Twoje konto — nie możesz zmienić jego roli ani go wyłączyć.
              </p>
            )}

            <Button variant="outline" size="sm" onClick={openRoleChange} disabled={isSelf}>
              <UserCog /> Zmień rolę
            </Button>

            {isDisabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setResetDone(false)
                  setError(null)
                  setAction('enable')
                }}
              >
                <Power /> Włącz konto
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setResetDone(false)
                  setError(null)
                  setAction('disable')
                }}
                disabled={isSelf}
              >
                <PowerOff /> Wyłącz konto
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setResetDone(false)
                setError(null)
                setAction('reset')
              }}
            >
              <KeyRound /> Resetuj hasło
            </Button>

            {canSetTemporaryPassword && (
              <Button variant="outline" size="sm" onClick={() => setTempPasswordOpen(true)}>
                <KeySquare /> Ustaw hasło tymczasowe
              </Button>
            )}

            {!isSelf && account.has_2fa && (
              <Button variant="outline" size="sm" onClick={() => setReset2FAOpen(true)}>
                <LockKeyholeOpen /> Zresetuj 2FA
              </Button>
            )}

            {resetDone && (
              <p className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
                Wysłano e-mail z linkiem do ustawienia nowego hasła.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* US3 — change role */}
      <ConfirmDialog
        open={action === 'role'}
        onOpenChange={(o) => (o ? setAction('role') : closeAction())}
        title="Zmień rolę konta"
        description={
          <div className="flex flex-col gap-3 pt-2">
            <span>
              Zmieniasz rolę konta <strong>{account.email}</strong>.
            </span>
            <Select value={nextRole} onValueChange={(v) => setNextRole(v as RoleEnum)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={RoleEnum.admin}>{roleLabel(RoleEnum.admin)}</SelectItem>
                <SelectItem value={RoleEnum.superadmin}>
                  {roleLabel(RoleEnum.superadmin)}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        confirmLabel="Zmień rolę"
        pending={pending}
        error={error}
        onConfirm={runRoleChange}
      />

      {/* US3 — disable */}
      <ConfirmDialog
        open={action === 'disable'}
        onOpenChange={(o) => (o ? setAction('disable') : closeAction())}
        title="Wyłącz konto"
        description={
          <>
            Wyłączenie konta <strong>{account.email}</strong> natychmiast zakończy jego sesje. Konto
            można później ponownie włączyć.
          </>
        }
        confirmLabel="Wyłącz konto"
        destructive
        pending={pending}
        error={error}
        onConfirm={runDisable}
      />

      {/* US3 — enable */}
      <ConfirmDialog
        open={action === 'enable'}
        onOpenChange={(o) => (o ? setAction('enable') : closeAction())}
        title="Włącz konto"
        description={
          <>
            Ponowne włączenie przywróci dostęp dla <strong>{account.email}</strong>. Użytkownik
            zaloguje się od nowa.
          </>
        }
        confirmLabel="Włącz konto"
        pending={pending}
        error={error}
        onConfirm={runEnable}
      />

      {/* US4 — password reset */}
      <ConfirmDialog
        open={action === 'reset'}
        onOpenChange={(o) => (o ? setAction('reset') : closeAction())}
        title="Resetuj hasło"
        description={
          <>
            Na adres <strong>{account.email}</strong> trafi e-mail z linkiem do ustawienia nowego
            hasła. Przy następnym logowaniu konto będzie musiało je zmienić.
          </>
        }
        confirmLabel="Wyślij e-mail resetujący"
        pending={pending}
        error={error}
        onConfirm={runReset}
      />

      {/* TR-116 — reset 2FA */}
      <Reset2FADialog
        targetId={account.id}
        targetEmail={account.email}
        targetNickname={account.nickname}
        open={reset2FAOpen}
        onOpenChange={setReset2FAOpen}
        onDone={refresh}
        onSessionEnded={() => window.location.assign('/login?expired=ok')}
      />

      <TemporaryPasswordDialog
        targetId={account.id}
        targetEmail={account.email}
        targetNickname={account.nickname}
        open={tempPasswordOpen}
        onOpenChange={setTempPasswordOpen}
        onDone={refresh}
        onSessionEnded={() => window.location.assign('/login?expired=ok')}
      />
    </>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[12rem_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  )
}
